import { forwardRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { enGB } from "date-fns/locale";
import { CalendarBlank, Clock } from "@phosphor-icons/react";
import "react-datepicker/dist/react-datepicker.css";
import "./DateTimePicker.css";

// Register en-GB (24h + Monday-first) so the calendar UI never falls back to
// the OS Arabic locale which renders ٠-٩ digits.
registerLocale("en-GB", enGB);

// Convert a string ("YYYY-MM-DD" or ISO) or Date → Date | null
function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "string") {
    // Bare "YYYY-MM-DD" — parse as local midnight (avoid UTC shift)
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const d = new Date(`${v}T00:00:00`);
      return isNaN(d.getTime()) ? null : d;
    }
    // Bare "HH:mm" — anchor to today so react-datepicker can render it
    if (/^\d{1,2}:\d{2}$/.test(v)) {
      const [hh, mm] = v.split(":");
      const t = new Date();
      t.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
      return t;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

// Custom trigger input matches .mz-input styling so pickers blend into the UI.
const TriggerInput = forwardRef(function TriggerInput(
  { value, onClick, placeholder, icon: Icon, testId, disabled },
  ref,
) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      ref={ref}
      data-testid={testId}
      className="mz-input flex items-center gap-2 !text-left w-full font-latin num cursor-pointer disabled:opacity-60"
      dir="ltr"
    >
      {Icon && <Icon size={16} weight="duotone" className="text-primary/70 shrink-0" />}
      <span className={value ? "text-white" : "text-white/40"}>
        {value || placeholder}
      </span>
    </button>
  );
});

/**
 * CustomDatePicker
 * @param {string|Date} value    — "YYYY-MM-DD" string or Date object.
 * @param {(iso:string)=>void} onChange  — emits "YYYY-MM-DD" (or "" if cleared).
 * @param {Date}   [minDate]
 * @param {string} [placeholder]
 * @param {string} [testId]
 * @param {boolean}[required]
 * @param {boolean}[disabled]
 */
export default function CustomDatePicker({
  value,
  onChange,
  minDate,
  placeholder = "اختر التاريخ",
  testId,
  disabled,
}) {
  const dateVal = toDate(value);
  return (
    <DatePicker
      selected={dateVal}
      onChange={(d) => {
        if (!d) return onChange?.("");
        const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        onChange?.(iso);
      }}
      locale="en-GB"
      dateFormat="yyyy-MM-dd"
      minDate={minDate}
      disabled={disabled}
      popperClassName="mz-datepicker-popper"
      calendarClassName="mz-datepicker-calendar"
      customInput={<TriggerInput icon={CalendarBlank} placeholder={placeholder} testId={testId} disabled={disabled} />}
      showPopperArrow={false}
    />
  );
}

/**
 * CustomTimePicker — 24-hour time picker.
 * @param {string|Date} value  — "HH:mm" string or Date.
 * @param {(hhmm:string)=>void} onChange  — emits "HH:mm".
 */
export function CustomTimePicker({
  value,
  onChange,
  placeholder = "اختر الوقت",
  testId,
  disabled,
  intervals = 1,
}) {
  const dateVal = toDate(value);
  return (
    <DatePicker
      selected={dateVal}
      onChange={(d) => {
        if (!d) return onChange?.("");
        onChange?.(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
      }}
      locale="en-GB"
      showTimeSelect
      showTimeSelectOnly
      timeFormat="HH:mm"
      timeIntervals={intervals}
      timeCaption="الوقت"
      dateFormat="HH:mm"
      disabled={disabled}
      popperClassName="mz-datepicker-popper"
      calendarClassName="mz-datepicker-calendar"
      customInput={<TriggerInput icon={Clock} placeholder={placeholder} testId={testId} disabled={disabled} />}
      showPopperArrow={false}
    />
  );
}
